import { useCallback, useMemo } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FileModel } from 'models';

import { apiClient } from 'core/api/globals';
import { NotificationsService } from 'core/notifications';

import { AuthenticationScheme } from 'features/auth/constants';
import { useAssertFetchPermitted } from 'features/auth/hooks';

/**
 * Provides mutation functions for file operations within an exam context.
 * @param examId - The ID of the current exam (for query invalidation).
 * @param setCurrentFile - Callback to update the currently selected file in the parent component.
 */
export function useMutationFiles(examId: number | null | undefined, setCurrentFile?: (file: FileModel | null) => void) {
  const queryClient = useQueryClient();
  const activeScheme = useAssertFetchPermitted([AuthenticationScheme.OIDC, AuthenticationScheme.SHARE]);

  const filesQueryKey = useMemo(() => (examId == null || examId === 0 ? null : ['files', 'by-exam-id', examId]), [examId]); // Define key for consistency

  const invalidateFilesQuery = useCallback(() => {
    if (filesQueryKey == null) return;

    queryClient.invalidateQueries({ queryKey: filesQueryKey });
  }, [queryClient, filesQueryKey]);

  const uploadFilesMutation = useMutation({
    mutationFn: async ({
      files,
      shouldSplitFiles,
      examId,
      patientId,
      categoryId,
    }: {
      files: File[];
      shouldSplitFiles: boolean;
      examId: number | null;
      patientId: number | null;
      categoryId: number | null;
    }) => {
      const uploadedFiles = await apiClient.filesClient.add(
        files,
        shouldSplitFiles,
        examId,
        patientId,
        categoryId,
        activeScheme === AuthenticationScheme.SHARE ? 'share-required' : 'msal-required',
      );

      return uploadedFiles;
    },
    onSuccess: (uploadedFiles) => {
      NotificationsService.displaySuccess(`${uploadedFiles.length} ${uploadedFiles.length === 1 ? 'file' : 'files'} uploaded successfully`);

      const examIds = uploadedFiles.map((f) => f.examId).filter((examId) => examId != null);
      for (const examId of examIds) {
        queryClient.setQueryData(['files', 'by-exam-id', examId], (oldData: FileModel[] | undefined) => {
          const newExamFiles = uploadedFiles.filter((f) => f.examId === examId);
          return oldData == null ? newExamFiles : [...oldData, ...newExamFiles];
        });
      }
    },
    onError: (error, { files, shouldSplitFiles, examId, patientId, categoryId }) => {
      NotificationsService.displayError('Failed to upload files');
      datadogLogs.logger.error(
        'File upload failed.',
        {
          files: files.map((f) => ({ fileName: f.name, size: f.size })),
          shouldSplitFiles,
          ...(examId != null ? { examId } : {}),
          ...(patientId != null ? { patientId } : {}),
          ...(categoryId != null ? { categoryId } : {}),
        },
        error instanceof Error ? error : undefined,
      );
    },
  });

  const renameFileMutation = useMutation({
    mutationFn: async (file: FileModel) => {
      await apiClient.filesClient.renameFile(file.id, [{ op: 'replace', path: '/fileName', value: file.fileName }]);
    },
    onSuccess: (_result, file) => {
      NotificationsService.displaySuccess('File renamed successfully');

      if (filesQueryKey != null) {
        queryClient.setQueryData(filesQueryKey, (oldData: FileModel[] | undefined) => {
          return oldData == null ? [file] : oldData.map((f) => (f.id === file.id ? file : f));
        });
      }
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: (fileId: number) => apiClient.filesClient.deleteFile(fileId, 'msal-required'),
    onSuccess: (_result, fileId) => {
      NotificationsService.displaySuccess('File deleted successfully');

      if (filesQueryKey != null) {
        queryClient.setQueryData(filesQueryKey, (oldData: FileModel[] | undefined) => {
          return oldData == null ? [] : oldData.filter((f) => f.id !== fileId);
        });
      }
    },
    onError: (error, fileId) => {
      NotificationsService.displayError('Failed to delete file');
      datadogLogs.logger.error('File deletion failed.', { examId, fileId }, error instanceof Error ? error : undefined);
    },
  });

  const changeCategoryMutation = useMutation({
    mutationFn: ({ fileId, categoryId }: { fileId: number; categoryId: number }) => apiClient.filesClient.changeCategory(fileId, categoryId),
    onSuccess: () => {
      NotificationsService.displaySuccess('Document category changed');
      invalidateFilesQuery();
    },
    onError: (error, { fileId, categoryId }) => {
      NotificationsService.displayError('Failed to change document category');
      datadogLogs.logger.error('Category change failed.', { examId, fileId, categoryId }, error instanceof Error ? error : undefined);
    },
  });

  const generateReportMutation = useMutation({
    // Although related to exam, it generates a file, so keeping it here for file list updates
    mutationFn: async ({
      fileId,
      examId: reportExamId, // Using examId passed to mutation
    }: {
      fileId: number;
      examId: number;
    }) => {
      const newFile = await apiClient.filesClient.generateReport(fileId, reportExamId);

      if (newFile == null) throw new Error('Failed to generate report: No file returned.');

      return newFile;
    },
    onSuccess: (newFile) => {
      NotificationsService.displaySuccess('Report generated successfully');
      invalidateFilesQuery();
      setCurrentFile?.(newFile); // Set new file as current immediately
    },
    onError: (error, { fileId, examId: reportExamId }) => {
      NotificationsService.displayError('Failed to generate report');
      datadogLogs.logger.error('Report generation failed.', { examId, fileId, reportExamId }, error instanceof Error ? error : undefined);
    },
  });

  const rotateFileMutation = useMutation({
    mutationFn: async ({ file, degrees }: { file: FileModel; degrees: number }) => {
      const rotatedFile = await apiClient.filesClient.rotate(file, degrees);

      if (rotatedFile == null) throw new Error('Failed to rotate file: No file returned.');

      return rotatedFile;
    },
    onSuccess: (rotatedFile: FileModel) => {
      NotificationsService.displaySuccess('File rotated successfully');

      if (filesQueryKey != null) {
        queryClient.setQueryData(filesQueryKey, (oldData: FileModel[] | undefined) => {
          if (!oldData) return [rotatedFile]; // Handle case where cache is empty
          // Ensure oldData is treated as an array before mapping
          const currentFiles = Array.isArray(oldData) ? oldData : [];
          return currentFiles.map(
            (file) => (file.id === rotatedFile.id ? rotatedFile : file), // Replace the old file with the new rotated file data
          );
        });
      }

      setCurrentFile?.(rotatedFile); // Update main viewer immediately
    },
    onError: (error, { file, degrees }) => {
      NotificationsService.displayError('Failed to rotate file');
      datadogLogs.logger.error('File rotation failed.', { examId, fileId: file.id, degrees }, error instanceof Error ? error : undefined);
      // Invalidate on error to refetch potentially correct state
      invalidateFilesQuery();
    },
  });

  const sendDocumentsMutation = useMutation({
    mutationFn: async (payload: { fileId: number; sendType: string; contact: string; destinationId?: number }) => {
      const success = await apiClient.filesClient.sendDocuments(payload.fileId, payload.sendType, payload.contact, payload.destinationId);

      if (success == null) throw new Error('Failed to send document: No response returned.');
    },
    onSuccess: () => {
      NotificationsService.displaySuccess('Document sent successfully');
    },
    onError: (error, { fileId, sendType, contact, destinationId }) => {
      NotificationsService.displayError('Document failed to send');
      datadogLogs.logger.error('Send document failed.', { examId, fileId, sendType, contact, destinationId }, error instanceof Error ? error : undefined);
    },
  });

  return {
    invalidateFilesQuery,
    uploadFilesMutation,
    renameFileMutation,
    deleteFileMutation,
    changeCategoryMutation,
    generateReportMutation,
    rotateFileMutation,
    sendDocumentsMutation,
  };
}
